/**
 * Mobility MVP: Complete Pelvic Tilt Test Component
 * Orchestrates the pelvic tilt video test flow
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { PelvicTiltIntro } from './PelvicTiltIntro';
import { PelvicTiltCameraSheet } from './PelvicTiltCameraSheet';
import { PelvicTiltUploading } from './PelvicTiltUploading';
import { PelvicTiltSuccess } from './PelvicTiltSuccess';
import { useUploadPelvicTilt } from '@/hooks/useMobility';
import { useCloudflareUpload } from '@/features/swing-lab/upload/useCloudflareUpload';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';

interface PelvicTiltTestProps {
  onComplete: () => void;
  onSkip?: () => void;
}

export function PelvicTiltTest({ onComplete, onSkip }: PelvicTiltTestProps) {
  const [step, setStep] = useState<'intro' | 'camera' | 'review' | 'uploading' | 'success' | 'error'>('intro');
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [result, setResult] = useState<'good_control' | 'limited_control' | 'no_control' | null>(null);
  const [mounted, setMounted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const { uploadVideo, isUploading } = useCloudflareUpload();
  const uploadPelvicTilt = useUploadPelvicTilt();
  const [assessmentDetails, setAssessmentDetails] = useState<{ evidence: string[]; confidence: number } | null>(null);
  const previewUrl = useMemo(() => (videoBlob ? URL.createObjectURL(videoBlob) : null), [videoBlob]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);
  
  // #region agent log
  console.log('[PelvicTiltTest] Render:', { step, hasResult: !!result, hasVideoBlob: !!videoBlob, mounted });
  // #endregion

  // Track mount to prevent immediate state changes
  useEffect(() => {
    setMounted(true);
    // #region agent log
    console.log('[PelvicTiltTest] Component mounted');
    // #endregion
    return () => {
      setMounted(false);
      // #region agent log
      console.log('[PelvicTiltTest] Component unmounting');
      // #endregion
    };
  }, []);

  const handleStart = useCallback(() => {
    // #region agent log
    console.log('[PelvicTiltTest] handleStart called', { mounted, stack: new Error().stack });
    // #endregion
    if (!mounted) {
      console.warn('[PelvicTiltTest] Ignoring handleStart - component not mounted');
      return;
    }
    setStep('camera');
  }, [mounted]);

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    } else {
      onComplete();
    }
  };

  const handleVideoReady = async (blob: Blob) => {
    setVideoBlob(blob);
    setErrorMessage(null);
    setAssessmentDetails(null);
    setStep('review');
  };

  const handleAnalyze = useCallback(async () => {
    if (!videoBlob) return;
    setErrorMessage(null);
    setStep('uploading');

    try {
      // Step 1: Get Cloudflare Stream direct upload URL
      const { data: uploadData, error: uploadError } = await supabase.functions.invoke('cloudflare-direct-upload');
      
      if (uploadError || !uploadData?.ok || !uploadData.uploadURL || !uploadData.uid) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadURL, uid } = uploadData;

      // Step 2: Upload video to Cloudflare Stream
      const formData = new FormData();
      formData.append('file', videoBlob, 'pelvic-tilt.webm');

      const uploadResponse = await fetch(uploadURL, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new Error(`Failed to upload video: ${errorText}`);
      }

      // Step 3: Call pelvic tilt assessment with the UID
      const assessment = await uploadPelvicTilt.mutateAsync({
        playbackId: uid,
        cfStreamUid: uid
      });

      setResult(assessment.pelvic_control);
      setAssessmentDetails({
        evidence: Array.isArray(assessment.evidence) ? assessment.evidence : [],
        confidence: typeof assessment.confidence === 'number' ? assessment.confidence : 0,
      });
      setStep('success');
    } catch (error: any) {
      console.error('Failed to process pelvic tilt video:', error);
      setErrorMessage(typeof error?.message === 'string' ? error.message : String(error));
      setStep('error');
    }
  }, [videoBlob, uploadPelvicTilt]);
  const handleContinue = () => {
    onComplete();
  };

  // #region agent log
  console.log('[PelvicTiltTest] Rendering step:', step);
  // #endregion
  
  if (step === 'intro') {
    // #region agent log
    console.log('[PelvicTiltTest] Rendering PelvicTiltIntro');
    // #endregion
    return <PelvicTiltIntro onStart={handleStart} onSkip={handleSkip} />;
  }

  if (step === 'camera') {
    // #region agent log
    console.log('[PelvicTiltTest] Rendering PelvicTiltCameraSheet');
    // #endregion
    return (
      <PelvicTiltCameraSheet
        isOpen={true}
        embedded={true}
        onClose={handleSkip}
        onVideoReady={handleVideoReady}
      />
    );
  }

  if (step === 'review') {
    return (
      <div className="min-h-full w-full bg-background p-6 flex flex-col gap-4">
        <div>
          <h2 className="text-xl font-semibold">Recording complete</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Preview your video, then upload it for analysis. Processing may take 15–60 seconds.
          </p>
          {videoBlob && (
            <p className="text-xs text-muted-foreground mt-2">
              File: {(videoBlob.size / (1024 * 1024)).toFixed(1)} MB • Type: {videoBlob.type || 'unknown'}
            </p>
          )}
        </div>

        {previewUrl ? (
          <div className="w-full max-w-xl">
            <video
              src={previewUrl}
              controls
              playsInline
              className="w-full rounded-lg border bg-black"
            />
            <p className="text-xs text-muted-foreground mt-2">
              If the preview is blank in your browser, you can still upload & analyze — Cloudflare will process it server-side.
            </p>
          </div>
        ) : null}

        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            onClick={() => {
              setVideoBlob(null);
              setResult(null);
              setErrorMessage(null);
              setStep('camera');
            }}
            type="button"
          >
            Retake
          </Button>
          <Button onClick={handleAnalyze} type="button">
            Upload & Analyze
          </Button>
        </div>
      </div>
    );
  }

  if (step === 'uploading') {
    return <PelvicTiltUploading />;
  }

  if (step === 'success' && result) {
    return <PelvicTiltSuccess result={result} details={assessmentDetails || undefined} onContinue={handleContinue} />;
  }

  if (step === 'error') {
    return (
      <div className="min-h-full w-full bg-background p-6 flex flex-col gap-4">
        <div>
          <h2 className="text-xl font-semibold">We hit a snag</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Your recording is saved locally. You can try uploading again.
          </p>
        </div>
        {errorMessage && (
          <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-40">
            {errorMessage}
          </pre>
        )}
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setStep('review')} type="button">
            Back to preview
          </Button>
          <Button onClick={handleAnalyze} type="button">
            Retry upload
          </Button>
        </div>
      </div>
    );
  }

  // #region agent log
  console.warn('[PelvicTiltTest] No component rendered, step:', step, 'result:', result);
  // #endregion
  return null;
}
